import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Search, Star, ExternalLink, LayoutGrid, Globe, Monitor,
  Settings, LogOut, User, Folder, ChevronDown, Zap,
  Rocket, Code, FileText, Link2, Palette, Shield, Database,
  BarChart3, MessageSquare, Mail, Video, Image, Music,
  Smartphone, Cpu, Cloud, ShoppingCart, CreditCard, Users,
  X, Megaphone, MousePointerClick, UserCog,
  TrendingUp, TrendingDown, DollarSign, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileDialog from '../components/ProfileDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  ExternalLink, LayoutGrid, Globe, Monitor, Folder, Zap,
  Rocket, Code, FileText, Link2, Palette, Shield, Database,
  BarChart3, MessageSquare, Mail, Video, Image, Music,
  Smartphone, Cpu, Cloud, ShoppingCart, CreditCard, Users,
  Star, Settings, Search, User
};

function getIcon(name, props = {}) {
  const IconComp = ICON_MAP[name] || ExternalLink;
  return <IconComp {...props} />;
}

export default function HubPage({ onAdminClick }) {
  const { user, logout, authHeaders } = useAuth();
  const [rawApps, setRawApps] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [clicks, setClicks] = useState({});
  const [announcement, setAnnouncement] = useState(null);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [profileOpen, setProfileOpen] = useState(false);
  const [kpi, setKpi] = useState(null);

  const apps = useMemo(() => rawApps.filter(app => {
    if (!app.allowed_roles) return true;
    const roles = Array.isArray(app.allowed_roles)
      ? app.allowed_roles
      : typeof app.allowed_roles === 'string'
        ? JSON.parse(app.allowed_roles)
        : [];
    return roles.includes(user?.role);
  }), [rawApps, user?.role]);

  const loadData = async () => {
    try {
      // Fetch apps from Supabase
      const { data: supabaseApps, error } = await supabase
        .from('transform_apps')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) {
        console.error('Supabase apps fetch error:', error);
        setRawApps([]);
      } else {
        setRawApps(supabaseApps || []);
      }

      // Fetch favorites, announcement, clicks from backend
      const headers = authHeaders();
      const [favsRes, annRes, clicksRes] = await Promise.all([
        axios.get(`${API}/favorites`, headers).catch(() => ({ data: [] })),
        axios.get(`${API}/announcements`, headers).catch(() => ({ data: {} })),
        axios.get(`${API}/clicks`, headers).catch(() => ({ data: {} })),
      ]);
      setFavorites(favsRes.data || []);
      if (annRes.data?.message) setAnnouncement(annRes.data);
      setClicks(clicksRes.data || {});

      // Fetch KPIs from Supabase for current month
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { data: kpiRows } = await supabase
        .from('club_kpis_live')
        .select('*')
        .eq('month', currentMonth)
        .order('total_members', { ascending: false })
        .limit(1);
      if (kpiRows && kpiRows.length > 0) setKpi(kpiRows[0]);
    } catch (err) {
      console.error('Load error:', err);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFavorite = async (appId) => {
    try {
      const res = await axios.post(`${API}/favorites`, { link_id: appId }, authHeaders());
      if (res.data.action === 'added') {
        setFavorites(prev => [...prev, appId]);
      } else {
        setFavorites(prev => prev.filter(id => id !== appId));
      }
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  const trackClick = async (app) => {
    const roles = Array.isArray(app.allowed_roles) ? app.allowed_roles : [];
    if (roles.length > 0 && !roles.includes(user?.role)) {
      toast.error("Vous n'avez pas accès à cette application.");
      return;
    }
    try {
      axios.post(`${API}/links/${app.id}/click`, {}, authHeaders());
      setClicks(prev => ({ ...prev, [app.id]: (prev[app.id] || 0) + 1 }));
    } catch (_) {}
    let url = app.url;
    const SSO_V2_SLUGS = ['tasks', 'media-buyin', 'profit'];
    if (SSO_V2_SLUGS.includes(app.slug)) {
      const w = window.open('', '_blank');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const resp = await fetch(`${API}/sso/link`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          });
          if (resp.ok) {
            const { token_hash } = await resp.json();
            url = `${app.url}#sso_token_hash=${token_hash}`;
          } else {
            url = app.url;
            toast.error("Session expirée — reconnectez-vous au Hub pour le SSO");
          }
        } else {
          url = app.url;
          toast.error("Session expirée — reconnectez-vous au Hub pour le SSO");
        }
      } catch (_) {
        url = app.url;
        toast.error("Session expirée — reconnectez-vous au Hub pour le SSO");
      }
      if (w) { w.location.href = url; } else { window.location.href = url; }
    } else {
      window.open(url, '_blank');
    }
  };

  // Extract unique categories from apps
  const categories = useMemo(() => {
    const catSet = new Set();
    apps.forEach(app => {
      if (app.category) catSet.add(app.category);
    });
    return Array.from(catSet);
  }, [apps]);

  const filteredApps = useMemo(() => {
    let result = apps;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q) ||
        (a.url || '').toLowerCase().includes(q)
      );
    }
    if (activeFilter === 'favorites') {
      result = result.filter(a => favorites.includes(a.id));
    } else if (activeFilter !== 'all') {
      result = result.filter(a => a.category === activeFilter);
    }
    return result;
  }, [apps, searchQuery, activeFilter, favorites]);

  const favCount = favorites.length;
  const totalApps = apps.length;

  return (
    <div className="min-h-screen bg-black" data-testid="hub-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4" data-testid="hub-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0A84FF] rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight text-white heading-tight">TRANSFORM</span>
          </div>

          {/* Search */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="hub-search-input"
                type="text"
                placeholder="Search apps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1C1C1E] border-white/10 text-white placeholder:text-white/30 h-9 rounded-lg focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
              />
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {['admin', 'super_admin'].includes(user?.role) && (
              <Button
                data-testid="admin-panel-button"
                onClick={onAdminClick}
                variant="ghost"
                className="text-white/60 hover:text-white hover:bg-white/5 gap-2"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Admin</span>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="user-menu-trigger"
                  variant="ghost"
                  className="flex items-center gap-2 text-white/80 hover:text-white hover:bg-white/5"
                >
                  <div className="w-7 h-7 rounded-full bg-[#2C2C2E] flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-white/60" />
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                  <ChevronDown className="w-3 h-3 text-white/40" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#2C2C2E] border-white/10 text-white min-w-[180px]">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-white/40">{user?.email}</p>
                  <Badge variant="secondary" className="mt-1 text-[9px] tag-label bg-[#0A84FF]/10 border-[#0A84FF]/20 text-[#0A84FF] hover:bg-[#0A84FF]/10">
                    {(user?.role || '—').toUpperCase()}
                  </Badge>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="profile-button"
                  onClick={() => setProfileOpen(true)}
                  className="text-white/80 focus:text-white focus:bg-white/5 cursor-pointer"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="text-[#FF453A] focus:text-[#FF453A] focus:bg-[#FF453A]/10 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Announcement Banner */}
      {announcement && !announcementDismissed && (
        <div
          className={`mx-6 mt-4 max-w-7xl lg:mx-auto rounded-xl px-5 py-3 flex items-center justify-between border animate-fade-in ${
            announcement.type === 'success' ? 'bg-[#30D158]/10 border-[#30D158]/20 text-[#30D158]' :
            announcement.type === 'warning' ? 'bg-[#FFD60A]/10 border-[#FFD60A]/20 text-[#FFD60A]' :
            announcement.type === 'danger' ? 'bg-[#FF453A]/10 border-[#FF453A]/20 text-[#FF453A]' :
            'bg-[#0A84FF]/10 border-[#0A84FF]/20 text-[#0A84FF]'
          }`}
          data-testid="announcement-banner"
        >
          <div className="flex items-center gap-3">
            <Megaphone className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-medium">{announcement.message}</p>
          </div>
          <button
            data-testid="dismiss-announcement"
            onClick={() => setAnnouncementDismissed(true)}
            className="p-1 rounded-lg hover:bg-white/5 transition-colors duration-150 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Mobile Search */}
      <div className="md:hidden px-6 pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            data-testid="hub-search-mobile"
            type="text"
            placeholder="Search apps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1C1C1E] border-white/10 text-white placeholder:text-white/30 h-10 rounded-lg focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* CEO Overview */}
        {kpi && (
          <section className="mb-10 animate-fade-in" data-testid="ceo-overview">
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-[#0A84FF]" />
              <h2 className="text-base font-semibold text-white tracking-tight">Hybrid Gym Versoix — Overview</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* MEMBRES ACTIFS */}
              <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl p-5 flex flex-col" data-testid="kpi-members">
                <div className="flex items-center justify-between mb-3">
                  <span className="tag-label text-white/40" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>MEMBRES ACTIFS</span>
                  <Users className="w-4 h-4 text-[#0A84FF]" />
                </div>
                <p className="text-2xl font-bold text-white kpi-number">{kpi.total_members}</p>
              </div>
              {/* REVENUS DU MOIS */}
              <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl p-5 flex flex-col" data-testid="kpi-revenue">
                <div className="flex items-center justify-between mb-3">
                  <span className="tag-label text-white/40" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>REVENUS DU MOIS</span>
                  <DollarSign className="w-4 h-4 text-[#30D158]" />
                </div>
                <p className="text-2xl font-bold text-white kpi-number">
                  {Number(kpi.total_revenue).toLocaleString('fr-CH')} <span className="text-sm font-normal text-white/40">CHF</span>
                </p>
              </div>
              {/* NET PROFIT */}
              <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl p-5 flex flex-col" data-testid="kpi-profit">
                <div className="flex items-center justify-between mb-3">
                  <span className="tag-label text-white/40" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>NET PROFIT</span>
                  {kpi.net_profit >= 0
                    ? <TrendingUp className="w-4 h-4 text-[#30D158]" />
                    : <TrendingDown className="w-4 h-4 text-[#FF453A]" />}
                </div>
                <p className={`text-2xl font-bold kpi-number ${kpi.net_profit >= 0 ? 'text-[#30D158]' : 'text-[#FF453A]'}`}>
                  {Number(kpi.net_profit).toLocaleString('fr-CH')} <span className="text-sm font-normal text-white/40">CHF</span>
                </p>
              </div>
              {/* ROAS */}
              <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl p-5 flex flex-col" data-testid="kpi-roas">
                <div className="flex items-center justify-between mb-3">
                  <span className="tag-label text-white/40" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>ROAS</span>
                  <BarChart3 className="w-4 h-4 text-[#FFD60A]" />
                </div>
                <p className="text-2xl font-bold text-white kpi-number">{Number(kpi.roas).toFixed(2)}</p>
              </div>
            </div>
            {kpi.last_synced_at && (
              <p className="text-xs text-white/30 mt-3" data-testid="kpi-sync-time">
                Dernière sync : {new Date(kpi.last_synced_at).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(kpi.last_synced_at).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </section>
        )}

        {/* Stats Row */}
        <div className="flex items-center gap-4 mb-8 animate-fade-in overflow-x-auto pb-2" data-testid="kpi-stats">
          <div className="flex-shrink-0 bg-[#2C2C2E] border border-[#30D158]/40 rounded-2xl px-5 py-3.5 min-w-[110px] text-center">
            <p className="tag-label text-[#30D158] mb-1.5" style={{ fontSize: '11px', letterSpacing: '0.06em', fontWeight: 600 }}>APPS</p>
            <p className="kpi-number text-xl text-white font-bold">{totalApps}</p>
          </div>
          <div className="flex-shrink-0 bg-[#2C2C2E] border border-[#0A84FF]/40 rounded-2xl px-5 py-3.5 min-w-[110px] text-center">
            <p className="tag-label text-[#0A84FF] mb-1.5" style={{ fontSize: '11px', letterSpacing: '0.06em', fontWeight: 600 }}>FAVORIS</p>
            <p className="kpi-number text-xl text-white font-bold">{favCount}</p>
          </div>
          {categories.length > 0 && (
            <div className="flex-shrink-0 bg-[#2C2C2E] border border-[#FF453A]/40 rounded-2xl px-5 py-3.5 min-w-[110px] text-center">
              <p className="tag-label text-[#FF453A] mb-1.5" style={{ fontSize: '11px', letterSpacing: '0.06em', fontWeight: 600 }}>CATEGORIES</p>
              <p className="kpi-number text-xl text-white font-bold">{categories.length}</p>
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 animate-fade-in stagger-1" data-testid="category-filters">
          <button
            data-testid="filter-all"
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              activeFilter === 'all'
                ? 'bg-[#0A84FF] text-white'
                : 'bg-[#1C1C1E] text-white/60 hover:text-white hover:bg-[#2C2C2E] border border-white/10'
            }`}
          >
            All
          </button>
          <button
            data-testid="filter-favorites"
            onClick={() => setActiveFilter('favorites')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 flex items-center gap-2 ${
              activeFilter === 'favorites'
                ? 'bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/30'
                : 'bg-[#1C1C1E] text-white/60 hover:text-white hover:bg-[#2C2C2E] border border-white/10'
            }`}
          >
            <Star className="w-3.5 h-3.5" />
            Favorites
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`filter-category-${cat}`}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                activeFilter === cat
                  ? 'bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/30'
                  : 'bg-[#1C1C1E] text-white/60 hover:text-white hover:bg-[#2C2C2E] border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Apps Grid */}
        {filteredApps.length === 0 ? (
          <div className="text-center py-20 animate-fade-in" data-testid="empty-state">
            <div className="w-16 h-16 bg-[#1C1C1E] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-7 h-7 text-white/20" />
            </div>
            <p className="text-white/60 text-base">
              {searchQuery ? 'No results found' : 'No apps available'}
            </p>
            <p className="text-white/30 text-sm mt-1">
              {searchQuery ? 'Try a different search' : 'Apps will appear here once configured'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="links-grid">
            {filteredApps.map((app, idx) => {
              const isFav = favorites.includes(app.id);
              const appColor = app.color || '#0A84FF';
              return (
                <div
                  key={app.id}
                  className={`link-card p-5 flex flex-col items-center text-center animate-fade-in stagger-${Math.min(idx + 1, 6)}`}
                  style={{ '--card-accent': appColor }}
                  data-testid={`link-card-${app.id}`}
                >
                  {/* Favorite top-right */}
                  <div className="absolute top-3 right-3">
                    <button
                      data-testid={`favorite-toggle-${app.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(app.id); }}
                      className="p-1.5 rounded-lg hover:bg-white/5 transition-colors duration-150"
                    >
                      <Star
                        className={`w-4 h-4 transition-colors duration-150 ${
                          isFav ? 'fill-[#FFD60A] text-[#FFD60A]' : 'text-white/20 hover:text-white/40'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Title centered */}
                  <h3 className="text-base font-semibold text-white tracking-tight mt-2 mb-1 px-4">
                    {app.name || app.title || 'Untitled'}
                  </h3>
                  {app.description && (
                    <p className="text-[13px] text-white/40 mb-4 line-clamp-2 px-2">{app.description}</p>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between w-full mt-auto pt-3 border-t border-white/5">
                    <div className="flex items-center gap-2">
                      {app.category && (
                        <Badge
                          variant="secondary"
                          className="tag-label bg-white/5 border-white/10 text-white/50 hover:bg-white/5 px-2 py-0.5 rounded-md text-[10px]"
                        >
                          {app.category}
                        </Badge>
                      )}
                      {clicks[app.id] > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-white/30" data-testid={`click-count-${app.id}`}>
                          <MousePointerClick className="w-3 h-3" />
                          {clicks[app.id]}
                        </span>
                      )}
                    </div>
                    <button
                      data-testid={`link-open-${app.id}`}
                      onClick={(e) => { e.stopPropagation(); trackClick(app); }}
                      className="flex items-center gap-1.5 text-[#0A84FF] text-[13px] font-medium hover:text-[#409CFF] transition-colors duration-150"
                    >
                      Open
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Profile Dialog */}
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
